import { forwardRef, useCallback, useRef } from 'react';
import classnames from 'classnames';

import { keyboardMatches, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { isEventFromInteractiveComponent } from '@hh.ru/magritte-common-mouse';
import { DisabledContext, useDisabled } from '@hh.ru/magritte-common-use-disabled';
import type { PolymorphicForwardRefRenderFunc, PolymorphicComponentWithRef } from '@hh.ru/magritte-types';
import { CardSkeleton } from '@hh.ru/magritte-ui-card/Skeleton';
import type { CardBorderRadius, CardPadding, CardProps } from '@hh.ru/magritte-ui-card/types';
import { IconDynamic } from '@hh.ru/magritte-ui-icon';
import { enrichWithSkeleton } from '@hh.ru/magritte-ui-skeleton';
import { TextDynamic } from '@hh.ru/magritte-ui-typography';

import styles from './card.less';

const genBorderRadiusClasses = (radiusProps: {
    TopLeft?: CardBorderRadius;
    TopRight?: CardBorderRadius;
    BottomLeft?: CardBorderRadius;
    BottomRight?: CardBorderRadius;
}) =>
    Object.entries(radiusProps).reduce<string[]>(
        (acc, [key, value]) =>
            value !== undefined ? [...acc, styles[`cardBorder${key as keyof typeof radiusProps}Radius${value}`]] : acc,
        []
    );

const genPaddingProps = (paddingProps: {
    Left?: CardPadding;
    Right?: CardPadding;
    Top?: CardPadding;
    Bottom?: CardPadding;
}) =>
    Object.entries(paddingProps).reduce<Record<string, number>>(
        (acc, [key, value]) => (value !== undefined ? { ...acc, [`padding${key}`]: value } : acc),
        {}
    );

const CardForwardRefRenderFunc: PolymorphicForwardRefRenderFunc<CardProps, 'div'> = (
    {
        Element = 'div',
        stretched = false,
        verticalStretched = false,
        actionCard = false,
        onClick,
        style,
        hoverStyle,
        disabled = false,
        showShadow = false,
        showShadowOnHover = false,
        showBorder = false,
        padding = 0,
        paddingLeft,
        paddingRight,
        paddingTop,
        paddingBottom,
        borderRadius = 0,
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        children,
        ...restProps
    },
    ref
) => {
    const isToneCard = style !== undefined;
    const forceDisabled = useDisabled();
    const hasFocusRef = useRef(false);
    const focusCallback = () => {
        hasFocusRef.current = true;
    };
    const blurCallback = () => {
        hasFocusRef.current = false;
    };
    const activationHandler = useCallback(
        (event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
            if (disabled || forceDisabled || !onClick) {
                return;
            }

            if (
                event.type === 'click' ||
                (event.type === 'keydown' &&
                    keyboardMatches((event as React.KeyboardEvent<HTMLElement>).nativeEvent, [
                        keyboardKeys.Enter,
                        keyboardKeys.Space,
                    ]) &&
                    hasFocusRef.current)
            ) {
                !isEventFromInteractiveComponent(event) && onClick(event);
            }
        },
        [onClick, disabled, forceDisabled]
    );
    const paddingProps = genPaddingProps({
        Left: paddingLeft,
        Right: paddingRight,
        Top: paddingTop,
        Bottom: paddingBottom,
    });
    const additionalProps: Record<string, unknown> = {};
    if (actionCard && !forceDisabled && !disabled) {
        additionalProps.tabIndex = 0;
    }

    if (ref) {
        additionalProps.ref = ref;
    }

    return (
        <DisabledContext.Provider value={forceDisabled || (!!disabled && actionCard)}>
            <Element
                {...restProps}
                {...additionalProps}
                onFocus={focusCallback}
                onBlur={blurCallback}
                onClick={activationHandler}
                onKeyDown={activationHandler}
                style={{ padding, ...paddingProps }}
                className={classnames(
                    styles.card,
                    styles[`cardBorderRadius${borderRadius}`],
                    isToneCard && styles[`card-style-${style}`],
                    hoverStyle && styles[`card-hover-style-${hoverStyle}`],
                    ...genBorderRadiusClasses({
                        TopLeft: borderTopLeftRadius,
                        TopRight: borderTopRightRadius,
                        BottomLeft: borderBottomLeftRadius,
                        BottomRight: borderBottomRightRadius,
                    }),

                    {
                        [styles.cardStretched]: stretched,
                        [styles.cardVerticalStretched]: verticalStretched,
                        [styles.cardTone]: isToneCard,
                        [styles.cardAction]: actionCard,
                        [styles.cardShowShadow]: showShadow && !isToneCard,
                        [styles.cardShadowOnHover]: showShadowOnHover && !isToneCard && actionCard,
                        [styles.cardWithBorder]: showBorder,
                        [styles.cardDisabled]: actionCard && !!disabled,
                    }
                )}
            >
                <IconDynamic stretched>
                    <div className={styles.flexContainer}>
                        <TextDynamic stretched>{children}</TextDynamic>
                    </div>
                </IconDynamic>
            </Element>
        </DisabledContext.Provider>
    );
};

const CardComponent: PolymorphicComponentWithRef<CardProps, 'div'> = forwardRef(CardForwardRefRenderFunc);

export const Card = enrichWithSkeleton(CardComponent, CardSkeleton);
